import React, { useEffect, useRef } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';

interface CodeLine {
  x: number;
  y: number;
  speed: number;
  opacity: number;
  text: string;
  fontSize: number;
  color: string;
  width: number;
  glowIntensity: number;
}

const CODE_SNIPPETS = [
  'import tensorflow as tf',
  'model = Sequential()',
  'model.fit(X_train, y_train)',
  'from sklearn import svm',
  'plt.plot(history)',
  'df = pd.read_csv(',
  'accuracy = 0.982',
  'optimizer="adam"',
  'loss="binary_crossentropy"',
  'n_estimators=100',
  'random_state=42',
  'epochs=50, batch_size=32',
  'y_pred = clf.predict(X)',
  'confusion_matrix(y, y_pred)',
  'roc_auc_score(y_test, preds)',
  'GridSearchCV(estimator, param)',
  'StandardScaler().fit_transform',
  'train_test_split(X, y)',
  'learning_rate=0.001',
  'dropout(rate=0.3)',
  'ReLU() activation',
  'BatchNormalization()',
  'torch.nn.Module',
  'forward(self, x):',
  'return self.fc(x)',
  'loss.backward()',
  'optimizer.step()',
  'np.mean(squared_errors)',
  'r2_score(y_true, y_pred)',
  'f1_score(y_true, y_pred)',
  'classification_report()',
  'LocalLLM().load_weights()',
  'RBI_compliant = True',
  'model = AutoModel.from_pretrained',
  'tokenizer.encode(text)',
  'attention_mask = [1,1,1,0]',
  'transformers.pipeline()',
  'feature_importances_',
  'shap.TreeExplainer(model)',
  'lime.LimeTabularExplainer',
  'cross_val_score(clf, X, y)',
  'pipeline = make_pipeline(',
  'PCA(n_components=0.95)',
  'KMeans(n_clusters=5)',
  'DBSCAN(eps=0.5)',
  'TSNE(n_components=2)',
  'async def predict():',
  'await model.inference()',
  'redis_client.get(key)',
  'docker-compose up -d',
  'kubectl apply -f',
  'terraform plan',
  'FastAPI() app =',
  '@app.post("/predict")',
  'pydantic.BaseModel',
  'response_model=Prediction',
  'mlflow.log_param()',
  'wandb.init(project=)',
  'dvc pull data.csv',
  'git commit -m "model v2"',
  'pytest -xvs tests/',
  'black --check src/',
  'mypy --strict src/',
  'flake8 --max-line-length=88',
];

const COLORS = [
  '#B45309',
  '#D97706',
  '#0369A1',
  '#0891B2',
  '#7C3AED',
  '#059669',
  '#0F172A',
];

export default function CodeRainBackground(): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const linesRef = useRef<CodeLine[]>([]);
  const frameRef = useRef<number>(0);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const rectRef = useRef<DOMRect | null>(null);
  const isVisibleRef = useRef(true);
  const isPausedRef = useRef(false);
  const reducedMotion = useReducedMotion();

  // Reduced motion: show a static decorative gradient instead
  if (reducedMotion) {
    return (
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse at 20% 30%, rgba(245,166,35,0.08) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 70%, rgba(56,189,248,0.06) 0%, transparent 50%)
          `,
        }}
      />
    );
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let ctx: CanvasRenderingContext2D | null = null;
    try {
      ctx = canvas.getContext('2d');
    } catch {
      return;
    }
    if (!ctx) return;

    let w = 0;
    let h = 0;
    let dpr = 1;

    function updateRect() {
      rectRef.current = canvas!.getBoundingClientRect();
    }

    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const parent = canvas!.parentElement;
      w = parent?.clientWidth || window.innerWidth;
      h = parent?.clientHeight || window.innerHeight;
      canvas!.width = w * dpr;
      canvas!.height = h * dpr;
      canvas!.style.width = `${w}px`;
      canvas!.style.height = `${h}px`;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      updateRect();
    }

    resize();
    updateRect();

    // Throttled mouse handler (max 30fps)
    let lastMouseTime = 0;
    const handleMouseMove = (e: MouseEvent) => {
      const now = performance.now();
      if (now - lastMouseTime < 33) return; // throttle to ~30fps
      lastMouseTime = now;

      const rect = rectRef.current;
      if (!rect) return;
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };

    const handleMouseLeave = () => {
      mouseRef.current = { x: -1000, y: -1000 };
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    // IntersectionObserver: pause when hero not visible
    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting;
      },
      { threshold: 0 }
    );
    if (canvas.parentElement) observer.observe(canvas.parentElement);

    // Visibility API: pause when tab hidden
    const handleVisibility = () => {
      isPausedRef.current = document.hidden;
    };
    document.addEventListener('visibilitychange', handleVisibility);

    function createLine(): CodeLine {
      const fontSize = Math.random() * 10 + 11;
      ctx!.font = `${fontSize}px 'Fira Code', 'Consolas', 'Monaco', monospace`;
      const text = CODE_SNIPPETS[Math.floor(Math.random() * CODE_SNIPPETS.length)];
      const width = ctx!.measureText(text).width;
      const color = COLORS[Math.floor(Math.random() * COLORS.length)];

      return {
        x: Math.random() * w,
        y: Math.random() * h * 1.5 - h * 0.25,
        speed: Math.random() * 0.4 + 0.15,
        opacity: Math.random() * 0.25 + 0.08,
        text,
        fontSize,
        color,
        width,
        glowIntensity: Math.random() * 0.5 + 0.5,
      };
    }

    // Wait for fonts before initializing lines so widths are correct
    async function init() {
      if (document.fonts) {
        try {
          await document.fonts.ready;
        } catch {
          // fallback: proceed after short delay
          await new Promise((r) => setTimeout(r, 200));
        }
      }
      const lineCount = Math.min(Math.floor(w / 14), 70); // reduced from 90 for perf
      linesRef.current = Array.from({ length: lineCount }, createLine);
      frameRef.current = requestAnimationFrame(draw);
    }

    function draw() {
      if (isPausedRef.current || !isVisibleRef.current) {
        frameRef.current = requestAnimationFrame(draw);
        return;
      }

      ctx!.clearRect(0, 0, w, h);

      // Subtle grid dots
      ctx!.fillStyle = 'rgba(148, 163, 184, 0.08)';
      const gridSize = 40;
      for (let gx = 0; gx < w; gx += gridSize) {
        for (let gy = 0; gy < h; gy += gridSize) {
          ctx!.beginPath();
          ctx!.arc(gx, gy, 1, 0, Math.PI * 2);
          ctx!.fill();
        }
      }

      const mouse = mouseRef.current;
      const lines = linesRef.current;

      for (const line of lines) {
        const dx = line.x - mouse.x;
        const dy = line.y - mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const maxDist = 180;
        let mouseBoost = 0;
        if (dist < maxDist) {
          mouseBoost = (1 - dist / maxDist) * 0.5;
        }

        const finalOpacity = Math.min(line.opacity + mouseBoost, 0.9);
        const glowSize = line.glowIntensity * (1 + mouseBoost * 3);

        ctx!.shadowColor = line.color;
        ctx!.shadowBlur = glowSize * 8;
        ctx!.font = `${line.fontSize}px 'Fira Code', 'Consolas', 'Monaco', monospace`;
        ctx!.fillStyle = line.color;
        ctx!.globalAlpha = finalOpacity;
        ctx!.fillText(line.text, line.x, line.y);
        ctx!.shadowBlur = 0;
        ctx!.globalAlpha = 1;

        line.y += line.speed;

        if (line.y > h + 30) {
          line.y = -30;
          line.x = Math.random() * w;
          line.text = CODE_SNIPPETS[Math.floor(Math.random() * CODE_SNIPPETS.length)];
          line.width = ctx!.measureText(line.text).width;
          line.opacity = Math.random() * 0.25 + 0.08;
          line.color = COLORS[Math.floor(Math.random() * COLORS.length)];
        }
      }

      // Optimized connections: only connect nearest 2 neighbors per line, not O(n²)
      ctx!.strokeStyle = 'rgba(245, 166, 35, 0.08)';
      ctx!.lineWidth = 0.5;
      const connectDist = 100;
      for (let i = 0; i < lines.length; i++) {
        let connections = 0;
        for (let j = i + 1; j < lines.length && connections < 2; j++) {
          const a = lines[i];
          const b = lines[j];
          const cdx = a.x - b.x;
          const cdy = a.y - b.y;
          const cdistSq = cdx * cdx + cdy * cdy;
          if (cdistSq < connectDist * connectDist) {
            const cdist = Math.sqrt(cdistSq);
            ctx!.globalAlpha = (1 - cdist / connectDist) * 0.15;
            ctx!.beginPath();
            ctx!.moveTo(a.x, a.y);
            ctx!.lineTo(b.x, b.y);
            ctx!.stroke();
            connections++;
          }
        }
      }
      ctx!.globalAlpha = 1;

      frameRef.current = requestAnimationFrame(draw);
    }

    init();

    const handleResize = () => {
      resize();
      const newCount = Math.min(Math.floor(w / 14), 70);
      if (linesRef.current.length < newCount) {
        while (linesRef.current.length < newCount) {
          linesRef.current.push(createLine());
        }
      } else if (linesRef.current.length > newCount) {
        linesRef.current = linesRef.current.slice(0, newCount);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibility);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      observer.disconnect();
    };
  }, [reducedMotion]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'auto',
      }}
    />
  );
}
